import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { createEncodedKey } from "@lululemon/ecom-common/src/util/helpers";
import FacetItem from "../facet-item/facet-item";
import {
  formatSizeGroups,
  titlesBySizeGroup,
  splitShoesSizes,
  getGenderFromTitle,
} from "./../../utils";
import styles from "./size-facets.module.scss";

const SizeFacets = ({
  shouldRenderWithTitles,
  refinements,
  enableVisualSizeStyling,
  shouldDisplayTruncatedFilters,
  facetItemProps,
  onSortMenuSelectClick,
  locale,
  category,
  categoryTitle,
  path,
}) => {
  const renderRefinementsWithTitles = () => {
    const sizesShoesSplitted = splitShoesSizes({
      locale,
      gender: getGenderFromTitle(categoryTitle),
      category,
      sizes: refinements,
      URLPath: path,
    });
    const sizeGroups = formatSizeGroups(sizesShoesSplitted);
    return Object.keys(sizeGroups).map((sizeGroupKey, keyIndex) => {
      const groupRefinements = sizeGroups?.[`${sizeGroupKey}`] || [];
      const { title, subtitle } = titlesBySizeGroup[`${sizeGroupKey}`] || {};

      const isFirstGroup = keyIndex === 0;

      return (
        <React.Fragment key={`sizeGroup${sizeGroupKey}`}>
          {!isFirstGroup && <hr className={styles.groupDivider} />}
          <li className={styles.titlesContainer}>
            {title && (
              <h3
                className={classNames("lll-text-body-2", styles.title, {
                  [styles.titleFirst]: isFirstGroup,
                  [styles.titleWithSubtitle]: subtitle,
                })}
              >
                {title}
              </h3>
            )}
            {subtitle && (
              <p className={`lll-text-body-3 ${styles.subtitle}`}>{subtitle}</p>
            )}
            <ul
              className={classNames("row lll-base", {
                [styles.noMargin]: enableVisualSizeStyling,
                [styles.truncatedContainer]: shouldDisplayTruncatedFilters,
              })}
              data-testid="size-group-list-container"
            >
              {groupRefinements.map((refinement, refinementIndex) => (
                <FacetItem
                  {...facetItemProps}
                  onSortMenuSelectClick={onSortMenuSelectClick}
                  refinement={refinement}
                  index={refinementIndex}
                  hasSizeDivider={false}
                  key={createEncodedKey({
                    ...refinement,
                    refinementIndex,
                  })}
                />
              ))}
            </ul>
          </li>
        </React.Fragment>
      );
    });
  };

  const renderRefinementsWithoutTitles = () => {
    let initialSizeGroup;
    let sizeGroupNumber;

    return refinements?.map((refinement, i) => {
      // SIZE GROUP FOR SIZE FILTERS
      const previousSizeGroup = sizeGroupNumber;
      // To avoid divider for the first refinement group (could be 1/2/3/4)
      if (!initialSizeGroup && refinement.sizeGroup) {
        initialSizeGroup = refinement.sizeGroup;
      }
      if (refinement.sizeGroup && refinement.sizeGroup !== previousSizeGroup) {
        sizeGroupNumber = refinement.sizeGroup;
      }

      const hasSizeDivider =
        enableVisualSizeStyling &&
        sizeGroupNumber !== initialSizeGroup &&
        sizeGroupNumber !== previousSizeGroup;

      return (
        <FacetItem
          {...facetItemProps}
          onSortMenuSelectClick={onSortMenuSelectClick}
          refinement={refinement}
          index={i}
          hasSizeDivider={hasSizeDivider}
          key={createEncodedKey({
            ...refinement,
            i,
          })}
        />
      );
    });
  };

  return shouldRenderWithTitles
    ? renderRefinementsWithTitles()
    : renderRefinementsWithoutTitles();
};

SizeFacets.propTypes = {
  shouldRenderWithTitles: PropTypes.bool.isRequired,
  refinements: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string,
      sizeGroup: PropTypes.number,
    }).isRequired
  ).isRequired,
  enableVisualSizeStyling: PropTypes.bool.isRequired,
  shouldDisplayTruncatedFilters: PropTypes.bool.isRequired,
  facetItemProps: PropTypes.shape({
    enableVisualColorStyling: PropTypes.bool,
    enableVisualSizeStyling: PropTypes.bool,
    isSort: PropTypes.bool.isRequired,
    isMultiSelect: PropTypes.bool.isRequired,
    hasNoFollow: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    primaryNumRefinements: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    isTrucatedFilterExpanded: PropTypes.bool.isRequired,
    shouldDisplayTruncatedFilters: PropTypes.bool,
  }),
  onSortMenuSelectClick: PropTypes.func,
  locale: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  categoryTitle: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
};

export default SizeFacets;
